import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  IconButton,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useAuth } from '../hooks/useAuth';

interface Contact {
  id: number;
  name: string;
  email?: string;
  phone?: string | null;
  phone_extension?: string | null;
}

interface FollowUpContact {
  email: string;
  label: string;
}

interface CrmCase {
  id: number;
  case_name?: string;
  firm_id?: number;
  firm?: { name: string };
  opportunity_id?: string | null;
  file_number?: string | null;
  claim_number?: string | null;
  opportunity_follow_up?: string | null;
  sales_stage_id?: number | null;
  sales_stage?: { id: number; name: string } | null;
  case_type?: string;
  service_type_id?: number;
  service_type?: { name: string };
  attorney_id?: number | null;
  paralegal_id?: number | null;
  attorney?: Contact | null;
  paralegal?: Contact | null;
  assigned_expert?: { name: string; email?: string } | null;
}

const line = (label: string, value?: string | null) => (
  <Box>
    <Typography
      variant='caption'
      color='text.secondary'
      sx={{ fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase' }}
    >
      {label}
    </Typography>
    <Typography variant='body1' sx={{ mt: 0.5 }}>{value || '—'}</Typography>
  </Box>
);

const contactLines = (person?: Contact | null) => {
  if (!person) return '—';
  const phone = [person.phone, person.phone_extension ? `x${person.phone_extension}` : '']
    .filter(Boolean)
    .join(' ');
  return [person.name, person.email, phone].filter(Boolean).join(' · ');
};

const CaseCrmPanel: React.FC<{ caseData: CrmCase; onSaved: () => void }> = ({ caseData, onSaved }) => {
  const { user } = useAuth();
  const isAdmin = user?.roles?.some((role: { name: string }) => role.name === 'admin') ?? false;
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [stages, setStages] = useState<{ id: number; name: string }[]>([]);
  const [opportunityTypes, setOpportunityTypes] = useState<{ id: number; name: string }[]>([]);
  const [caseTypes, setCaseTypes] = useState<{ id: number; name: string }[]>([]);
  const [members, setMembers] = useState<Array<Contact & { designation?: { name: string } }>>([]);
  const [nudgeOpen, setNudgeOpen] = useState(false);
  const [sendingNudge, setSendingNudge] = useState(false);
  const [contacts, setContacts] = useState<FollowUpContact[]>([]);
  const [choices, setChoices] = useState<Record<string, '' | 'to' | 'cc'>>({});
  const [extraTo, setExtraTo] = useState('');
  const [extraCc, setExtraCc] = useState('');
  const [nudgeSubject, setNudgeSubject] = useState('');
  const [nudgeBody, setNudgeBody] = useState('');
  const [form, setForm] = useState({
    opportunity_id: '',
    file_number: '',
    claim_number: '',
    sales_stage_id: '',
    opportunity_follow_up: '',
    attorney_id: '',
    paralegal_id: '',
    case_type: '',
    service_type_id: '',
  });

  const openEditor = async () => {
    setForm({
      opportunity_id: caseData.opportunity_id || '',
      file_number: caseData.file_number || '',
      claim_number: caseData.claim_number || '',
      sales_stage_id: caseData.sales_stage_id ? String(caseData.sales_stage_id) : '',
      opportunity_follow_up: caseData.opportunity_follow_up || '',
      attorney_id: caseData.attorney_id ? String(caseData.attorney_id) : '',
      paralegal_id: caseData.paralegal_id ? String(caseData.paralegal_id) : '',
      case_type: caseData.case_type || '',
      service_type_id: caseData.service_type_id ? String(caseData.service_type_id) : '',
    });
    setOpen(true);
    const [stageRes, typeRes] = await Promise.all([
      api.get('/admin/masters/sales-stages', { params: { active_only: 1 } }),
      api.get('/admin/masters/opportunity-case-types', { params: { active_only: 1 } }),
    ]);
    setStages(stageRes.data.data ?? []);
    setOpportunityTypes(typeRes.data.data ?? []);
    if (caseData.firm_id) {
      const memberRes = await api.get(`/admin/firms/${caseData.firm_id}/members`);
      setMembers(memberRes.data.data ?? []);
    }
  };

  useEffect(() => {
    if (!form.case_type) {
      setCaseTypes([]);
      return;
    }
    api.get('/admin/service-types', { params: { case_type: form.case_type } })
      .then(res => setCaseTypes(Array.isArray(res.data) ? res.data : []))
      .catch(() => setCaseTypes([]));
  }, [form.case_type, open]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/cases/${caseData.id}/crm-details`, {
        file_number: form.file_number,
        claim_number: form.claim_number,
        attorney_id: form.attorney_id || null,
        paralegal_id: form.paralegal_id || null,
        case_type: form.case_type,
        service_type_id: form.service_type_id || null,
        ...(isAdmin ? {
          opportunity_id: form.opportunity_id,
          sales_stage_id: form.sales_stage_id || null,
          opportunity_follow_up: form.opportunity_follow_up,
        } : {}),
      });
      toast.success('Case information saved');
      setOpen(false);
      onSaved();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to save case information');
    } finally {
      setSaving(false);
    }
  };

  const openNudge = async () => {
    setNudgeSubject(`Follow up: ${caseData.case_name || 'case'}`);
    setNudgeBody(caseData.opportunity_follow_up || '');
    setExtraTo('');
    setExtraCc('');
    setChoices({});
    setContacts([]);
    setNudgeOpen(true);
    try {
      const res = await api.get(`/cases/${caseData.id}/follow-up-recipients`);
      const list: FollowUpContact[] = res.data.data ?? [];
      setContacts(list);
      setChoices(Object.fromEntries(list.map(contact => [contact.email, ''])));
    } catch {
      toast.error('Could not load case contacts. You can still type email addresses.');
    }
  };

  const setAllChoices = (choice: '' | 'to' | 'cc') => {
    setChoices(Object.fromEntries(contacts.map(contact => [contact.email, choice])));
  };

  const parseEmails = (value: string) =>
    value.split(/[\s,;]+/).map(email => email.trim()).filter(Boolean);

  const sendNudge = async () => {
    const to = [
      ...contacts.filter(contact => choices[contact.email] === 'to').map(contact => contact.email),
      ...parseEmails(extraTo),
    ];
    const cc = [
      ...contacts.filter(contact => choices[contact.email] === 'cc').map(contact => contact.email),
      ...parseEmails(extraCc),
    ];
    if (!nudgeSubject.trim()) {
      toast.error('Add a subject.');
      return;
    }
    if (!nudgeBody.trim()) {
      toast.error('Write the email body.');
      return;
    }
    if (!to.length) {
      toast.error('Choose at least one To recipient.');
      return;
    }
    setSendingNudge(true);
    try {
      await api.post(`/cases/${caseData.id}/follow-up`, {
        subject: nudgeSubject.trim(),
        body: nudgeBody,
        to,
        cc,
      });
      toast.success('Follow-up email sent');
      setNudgeOpen(false);
      onSaved();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to send follow-up');
    } finally {
      setSendingNudge(false);
    }
  };

  const attorneys = members.filter(member => member.designation?.name === 'Attorney');
  const paralegals = members.filter(member => member.designation?.name === 'Paralegal');

  return (
    <Box>
      <Card
        sx={{
          borderRadius: 2,
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: theme => theme.palette.mode === 'light' ? 'grey.50' : 'action.hover',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Avatar
              variant='rounded'
              sx={{
                width: 38,
                height: 38,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
              }}
            >
              <InfoOutlinedIcon fontSize='small' />
            </Avatar>
            <Box>
              <Typography variant='subtitle1' fontWeight={700} lineHeight={1.3}>
                Case Information
              </Typography>
              <Typography variant='caption' color='text.secondary' display='block'>
                Opportunity, account, and case contacts
              </Typography>
            </Box>
          </Box>
          <Button size='small' variant='outlined' onClick={openEditor}>Edit</Button>
        </Box>
        <CardContent sx={{ pt: 2.5, '&:last-child': { pb: 2.5 } }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2.5,
            }}
          >
            {isAdmin && line('Opportunity ID', caseData.opportunity_id)}
            {line('File Number', caseData.file_number)}
            {line('Claim Number', caseData.claim_number)}
            {line('Expert Name', caseData.assigned_expert?.name)}
            {line('Account Name', caseData.firm?.name)}
            {isAdmin && line('Sales Stage', caseData.sales_stage?.name)}
            {line('Opportunity Case Type', caseData.case_type)}
            {line('Case Type', caseData.service_type?.name)}
            {line('Paralegal', contactLines(caseData.paralegal))}
            {line('Attorney', contactLines(caseData.attorney))}
            {isAdmin && (
              <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
                <Stack direction='row' alignItems='center' spacing={0.5}>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase' }}
                  >
                    Opportunity Follow Up
                  </Typography>
                  <Tooltip title='Send follow-up email'>
                    <IconButton size='small' aria-label='Send follow-up email' onClick={openNudge}>
                      <NotificationsActiveIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Typography variant='body1' sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                  {caseData.opportunity_follow_up || '—'}
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth='sm'>
        <DialogTitle>Edit Case Information</DialogTitle>
        <DialogContent>
          {isAdmin && <TextField fullWidth margin='dense' label='Opportunity ID' value={form.opportunity_id} onChange={e => setForm(f => ({ ...f, opportunity_id: e.target.value }))} />}
          <TextField fullWidth margin='dense' label='File Number' value={form.file_number} onChange={e => setForm(f => ({ ...f, file_number: e.target.value }))} />
          <TextField fullWidth margin='dense' label='Claim Number' value={form.claim_number} onChange={e => setForm(f => ({ ...f, claim_number: e.target.value }))} />
          {isAdmin && (
          <FormControl fullWidth margin='dense'>
            <InputLabel>Sales Stage</InputLabel>
            <Select label='Sales Stage' value={form.sales_stage_id} onChange={e => setForm(f => ({ ...f, sales_stage_id: e.target.value }))}>
              <MenuItem value=''>Select</MenuItem>
              {stages.map(stage => <MenuItem key={stage.id} value={String(stage.id)}>{stage.name}</MenuItem>)}
            </Select>
          </FormControl>
          )}
          <FormControl fullWidth margin='dense'>
            <InputLabel>Opportunity Case Type</InputLabel>
            <Select label='Opportunity Case Type' value={form.case_type} onChange={e => setForm(f => ({ ...f, case_type: e.target.value, service_type_id: '' }))}>
              {opportunityTypes.map(type => <MenuItem key={type.id} value={type.name}>{type.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth margin='dense'>
            <InputLabel>Case Type</InputLabel>
            <Select label='Case Type' value={form.service_type_id} onChange={e => setForm(f => ({ ...f, service_type_id: e.target.value }))}>
              {caseTypes.map(type => <MenuItem key={type.id} value={String(type.id)}>{type.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth margin='dense'>
            <InputLabel>Attorney</InputLabel>
            <Select label='Attorney' value={form.attorney_id} onChange={e => setForm(f => ({ ...f, attorney_id: e.target.value }))}>
              <MenuItem value=''>None</MenuItem>
              {attorneys.map(member => <MenuItem key={member.id} value={String(member.id)}>{member.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth margin='dense'>
            <InputLabel>Paralegal</InputLabel>
            <Select label='Paralegal' value={form.paralegal_id} onChange={e => setForm(f => ({ ...f, paralegal_id: e.target.value }))}>
              <MenuItem value=''>None</MenuItem>
              {paralegals.map(member => <MenuItem key={member.id} value={String(member.id)}>{member.name}</MenuItem>)}
            </Select>
          </FormControl>
          {isAdmin && (
          <TextField fullWidth margin='dense' label='Opportunity Follow Up' multiline minRows={2} value={form.opportunity_follow_up} onChange={e => setForm(f => ({ ...f, opportunity_follow_up: e.target.value }))} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant='contained' onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={nudgeOpen} onClose={() => !sendingNudge && setNudgeOpen(false)} fullWidth maxWidth='sm'>
        <DialogTitle>Send follow-up</DialogTitle>
        <DialogContent>
          <Stack direction='row' spacing={1} sx={{ mt: 1, mb: 1 }}>
            <Button size='small' variant='outlined' onClick={() => setAllChoices('to')} disabled={!contacts.length}>Everyone To</Button>
            <Button size='small' variant='outlined' onClick={() => setAllChoices('cc')} disabled={!contacts.length}>Everyone Cc</Button>
            <Button size='small' onClick={() => setAllChoices('')} disabled={!contacts.length}>Clear</Button>
          </Stack>
          <Stack spacing={1} sx={{ mb: 1 }}>
            {contacts.map(contact => (
              <Stack key={contact.email} direction='row' alignItems='center' justifyContent='space-between' spacing={1}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant='body2' noWrap>{contact.label}</Typography>
                  <Typography variant='caption' color='text.secondary' noWrap>{contact.email}</Typography>
                </Box>
                <ToggleButtonGroup
                  exclusive
                  size='small'
                  value={choices[contact.email] || null}
                  onChange={(_, value) => setChoices(current => ({ ...current, [contact.email]: value || '' }))}
                >
                  <ToggleButton value='to'>To</ToggleButton>
                  <ToggleButton value='cc'>Cc</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            ))}
            {!contacts.length && (
              <Typography variant='body2' color='text.secondary'>No case contacts with an email address.</Typography>
            )}
          </Stack>
          <TextField fullWidth margin='dense' label='Other To emails' placeholder='name@firm.com, other@firm.com' value={extraTo} onChange={e => setExtraTo(e.target.value)} />
          <TextField fullWidth margin='dense' label='Other Cc emails' placeholder='name@firm.com, other@firm.com' value={extraCc} onChange={e => setExtraCc(e.target.value)} />
          <TextField fullWidth margin='dense' label='Subject' value={nudgeSubject} onChange={e => setNudgeSubject(e.target.value)} />
          <TextField fullWidth margin='dense' label='Email body' multiline minRows={5} value={nudgeBody} onChange={e => setNudgeBody(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNudgeOpen(false)} disabled={sendingNudge}>Cancel</Button>
          <Button variant='contained' onClick={sendNudge} disabled={sendingNudge}>{sendingNudge ? 'Sending…' : 'Send'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CaseCrmPanel;
